import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { TrendingUp, DollarSign, PiggyBank, Clock, Target, Users, ChevronRight, ChevronLeft, Download, Mail, Shield, BarChart3, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => (
  <header className="sticky top-0 z-40 w-full border-b border-border/40 bg-white/80 backdrop-blur-xl">
    <div className="app-container flex h-16 items-center justify-between">
      <div className="flex items-center gap-3">
        <img 
          src="https://customer-assets.emergentagent.com/job_margin-compass-1/artifacts/6le1ffnr_573A34B3-F32D-4F63-B1D8-E89334E6F0DB.jpeg" 
          alt="Finnovate Advisors" 
          className="h-10 w-auto"
        />
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Shield className="w-4 h-4" />
        <span>Your data stays private</span>
      </div>
    </div>
  </header>
);

// Currency Input Component with formatting
const CurrencyInput = ({ id, label, value, onChange, placeholder, tooltip }) => {
  const [displayValue, setDisplayValue] = useState(value ? Number(value).toLocaleString('en-US') : '');
  const [isFocused, setIsFocused] = useState(false);

  // Format number with commas
  const formatNumber = (num) => {
    if (!num && num !== 0) return '';
    const number = parseFloat(num);
    if (isNaN(number)) return '';
    return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  // Parse formatted string back to number
  const parseNumber = (str) => {
    if (!str) return '';
    const cleaned = str.replace(/,/g, '');
    const number = parseFloat(cleaned);
    return isNaN(number) ? '' : number;
  };

  // Update display when value prop changes
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value ? formatNumber(value) : '');
    }
  }, [value, isFocused]);

  const handleChange = (e) => {
    const input = e.target.value;
    // Allow only numbers, commas, and decimal point
    const cleaned = input.replace(/[^0-9.,]/g, '');
    setDisplayValue(cleaned);
    
    // Parse and send raw number to parent
    const numericValue = parseNumber(cleaned);
    onChange(numericValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Show raw number on focus for easier editing
    if (value) {
      setDisplayValue(value.toString());
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Format with commas on blur
    if (value) {
      setDisplayValue(formatNumber(value));
    } else {
      setDisplayValue('');
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-zinc-700">
        {label}
        {tooltip && <span className="text-muted-foreground text-xs ml-1">({tooltip})</span>}
      </Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono">$</span>
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="pl-7 h-12 font-mono text-base"
          data-testid={`input-${id}`}
        />
      </div>
    </div>
  );
};

// Step Progress Component
const StepProgress = ({ currentStep, totalSteps }) => (
  <div className="flex items-center gap-2">
    {[...Array(totalSteps)].map((_, i) => (
      <div
        key={i}
        className={`h-1.5 rounded-full transition-all duration-300 ${
          i < currentStep ? "w-8 bg-[#3A5A40]" : i === currentStep ? "w-8 bg-[#3A5A40]" : "w-4 bg-zinc-200"
        }`}
      />
    ))}
  </div>
);

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, context, status, delay }) => {
  const statusColors = {
    good: "text-[#65A30D]",
    warning: "text-[#EA580C]",
    critical: "text-[#DC2626]",
  };

  return (
    <Card 
      className={`animate-slide-up opacity-0 border-border/60 hover:border-[#3A5A40]/30 transition-colors duration-200`}
      style={{ animationDelay: `${delay * 0.05}s`, animationFillMode: 'forwards' }}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="p-2 rounded-lg bg-zinc-100">
            <Icon className="w-5 h-5 text-[#3A5A40]" />
          </div>
          {status && (
            <div className={`w-2 h-2 rounded-full ${
              status === 'good' ? 'bg-[#65A30D]' : 
              status === 'warning' ? 'bg-[#EA580C]' : 'bg-[#DC2626]'
            }`} />
          )}
        </div>
        <p className="text-sm font-medium text-zinc-500 mb-1">{label}</p>
        <p className={`text-2xl font-mono font-medium ${statusColors[status] || 'text-zinc-900'}`}>
          {value}
        </p>
        {context && <p className="text-xs text-zinc-400 mt-2">{context}</p>}
      </CardContent>
    </Card>
  );
};

// Health Score Component
const HealthScore = ({ score, value }) => {
  const config = {
    Strong: { color: "bg-[#ECFCCB] text-[#65A30D]", progress: 100 },
    Stable: { color: "bg-[#FFEDD5] text-[#EA580C]", progress: 66 },
    "At Risk": { color: "bg-[#FEF2F2] text-[#DC2626]", progress: 33 },
  };

  const { color, progress } = config[score] || config.Stable;

  return (
    <Card className="animate-slide-up opacity-0 border-2 border-[#3A5A40]/20" style={{ animationDelay: '0.05s', animationFillMode: 'forwards' }} data-testid="health-score-card">
      <CardContent className="p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="text-sm font-medium text-zinc-500 mb-2">Overall Financial Health</p>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full font-semibold ${color}`} data-testid="health-score-badge">
                {score}
              </span>
              <span className="text-3xl font-mono font-semibold text-zinc-900" data-testid="health-score-value">{value}/100</span>
            </div>
          </div>
          <div className="flex-1 max-w-xs">
            <div className="flex justify-between text-xs text-zinc-500 mb-2">
              <span>At Risk</span>
              <span>Stable</span>
              <span>Strong</span>
            </div>
            <Progress value={value} className="h-2" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Recommendation Card Component
const RecommendationCard = ({ recommendation, delay }) => {
  const priorityConfig = {
    critical: { border: "border-l-[#DC2626]", badge: "bg-[#FEF2F2] text-[#DC2626]" },
    high: { border: "border-l-[#EA580C]", badge: "bg-[#FFEDD5] text-[#EA580C]" },
    medium: { border: "border-l-[#3A5A40]", badge: "bg-[#DAD7CD] text-[#344E41]" },
    low: { border: "border-l-zinc-400", badge: "bg-zinc-100 text-zinc-600" },
  };

  const { border, badge } = priorityConfig[recommendation.priority] || priorityConfig.medium;

  return (
    <Card 
      className={`animate-slide-up opacity-0 border-l-4 ${border}`}
      style={{ animationDelay: `${delay * 0.1}s`, animationFillMode: 'forwards' }}
      data-testid={`recommendation-${recommendation.category.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h4 className="font-semibold text-zinc-900">{recommendation.title}</h4>
          <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${badge}`}>
            {recommendation.priority}
          </span>
        </div>
        <p className="text-sm text-zinc-600 mb-2">{recommendation.description}</p>
        <p className="text-xs text-[#3A5A40] font-medium">{recommendation.impact}</p>
      </CardContent>
    </Card>
  );
};

// Email Modal Component
const EmailModal = ({ open, onOpenChange, analysisId, onSuccess }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) {
      toast.error("Please enter your first name");
      return;
    }
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/submit-email`, { first_name: firstName.trim(), email, analysis_id: analysisId });
      toast.success("Check your email for your report and results summary!");
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="email-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#3A5A40]" />
            Get Your Report
          </DialogTitle>
          <DialogDescription>
            Enter your details to receive your personalized financial health report. We'll also send a summary to your email.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              type="text"
              placeholder="Your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid="first-name-input"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                data-testid="email-input"
                required
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-[#3A5A40] hover:bg-[#344E41]" data-testid="submit-email-btn">
              {loading ? "Sending..." : "Get My Report"}
            </Button>
          </div>
          <p className="text-xs text-center text-zinc-500">
            We respect your privacy. No spam, ever.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Financial Input Form Component
const FinancialInputForm = ({ onAnalyze }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    monthly_revenue: "",
    cost_of_goods_sold: "",
    monthly_expenses: "",
    owner_compensation: "",
    cash_balance: "",
    accounts_receivable: "",
    debt_payments: "",
    largest_customer_revenue: "",
  });

  const steps = [
    {
      title: "Revenue & Costs",
      description: "Tell us about your income and direct costs",
      fields: [
        { id: "monthly_revenue", label: "Monthly Revenue", tooltip: "Total monthly sales" },
        { id: "cost_of_goods_sold", label: "Cost of Goods Sold", tooltip: "Direct product/service costs" },
      ],
    },
    {
      title: "Operating Expenses",
      description: "Your recurring business expenses",
      fields: [
        { id: "monthly_expenses", label: "Operating Expenses", tooltip: "Rent, utilities, marketing, etc." },
        { id: "owner_compensation", label: "Owner Compensation", tooltip: "Your salary or draws" },
        { id: "debt_payments", label: "Monthly Debt Payments", tooltip: "Loan payments, credit lines" },
      ],
    },
    {
      title: "Cash Position",
      description: "Your current financial reserves and receivables",
      fields: [
        { id: "cash_balance", label: "Cash Balance", tooltip: "Bank accounts and cash on hand" },
        { id: "accounts_receivable", label: "Accounts Receivable", tooltip: "Money owed to you" },
        { id: "largest_customer_revenue", label: "Largest Customer Revenue", tooltip: "Optional: Revenue from biggest client" },
      ],
    },
  ];

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    // Validate step 1 required field
    if (step === 0 && !formData.monthly_revenue) {
      toast.error("Please enter your monthly revenue");
      return;
    }
    // Validate step 2 required field
    if (step === 1 && !formData.monthly_expenses) {
      toast.error("Please enter your operating expenses");
      return;
    }
    if (step < steps.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.monthly_revenue || !formData.monthly_expenses) {
      toast.error("Please enter at least monthly revenue and expenses");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        monthly_revenue: parseFloat(formData.monthly_revenue) || 0,
        cost_of_goods_sold: parseFloat(formData.cost_of_goods_sold) || 0,
        monthly_expenses: parseFloat(formData.monthly_expenses) || 0,
        owner_compensation: parseFloat(formData.owner_compensation) || 0,
        cash_balance: parseFloat(formData.cash_balance) || 0,
        accounts_receivable: parseFloat(formData.accounts_receivable) || 0,
        debt_payments: parseFloat(formData.debt_payments) || 0,
        largest_customer_revenue: formData.largest_customer_revenue ? parseFloat(formData.largest_customer_revenue) : null,
      };

      const response = await axios.post(`${API}/analyze`, payload);
      toast.success("Analysis complete!");
      onAnalyze(response.data);
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  const currentStep = steps[step];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 py-12 md:py-20">
        <div className="app-container max-w-2xl">
          {/* Hero Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl sm:text-5xl font-light text-zinc-900 mb-4">
              Assess Your <span className="font-semibold">Financial Health</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-500 max-w-lg mx-auto">
              Get instant insights into your business profitability, cash runway, and actionable recommendations.
            </p>
          </div>

          {/* Form Card */}
          <Card className="animate-slide-up border-border/60" data-testid="input-form-card">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between mb-4">
                <StepProgress currentStep={step} totalSteps={steps.length} />
                <span className="text-sm text-zinc-500">Step {step + 1} of {steps.length}</span>
              </div>
              <CardTitle className="text-xl">{currentStep.title}</CardTitle>
              <CardDescription>{currentStep.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {currentStep.fields.map((field) => (
                <CurrencyInput
                  key={field.id}
                  id={field.id}
                  label={field.label}
                  tooltip={field.tooltip}
                  value={formData[field.id]}
                  onChange={(value) => updateField(field.id, value)}
                  placeholder="0.00"
                />
              ))}

              <div className="flex gap-3 pt-4">
                {step > 0 && (
                  <Button type="button" variant="outline" onClick={handleBack} className="flex-1" data-testid="back-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                )}
                {step < steps.length - 1 ? (
                  <Button type="button" onClick={handleNext} className="flex-1 bg-[#3A5A40] hover:bg-[#344E41]" data-testid="next-btn">
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button 
                    type="button" 
                    onClick={handleSubmit} 
                    disabled={loading} 
                    className="flex-1 bg-[#3A5A40] hover:bg-[#344E41]"
                    data-testid="analyze-btn"
                  >
                    {loading ? "Analyzing..." : "Analyze My Business"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-zinc-500">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Secure & Private</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Instant Results</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ analysis, onReset }) => {
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const downloadReport = () => {
    window.open(`${API}/generate-report/${analysis.id}`, "_blank");
  };

  const kpiConfig = [
    { icon: TrendingUp, label: "Gross Margin", value: `${analysis.metrics.gross_margin}%`, status: analysis.insights[0]?.status },
    { icon: DollarSign, label: "Net Profit Margin", value: `${analysis.metrics.net_profit_margin}%`, status: analysis.insights[1]?.status },
    { icon: PiggyBank, label: "Monthly Profit", value: `$${analysis.metrics.monthly_profit.toLocaleString()}`, status: analysis.insights[2]?.status },
    { icon: Clock, label: "Cash Runway", value: analysis.metrics.cash_runway_months >= 999 ? "999+ mo" : `${analysis.metrics.cash_runway_months} mo`, status: analysis.insights[3]?.status },
    { icon: Target, label: "Break-even Revenue", value: `$${analysis.metrics.break_even_revenue.toLocaleString()}`, status: analysis.insights[4]?.status },
    { icon: Users, label: "Revenue Concentration", value: analysis.metrics.revenue_concentration_risk, status: analysis.insights[5]?.status },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF9]">
      <Header />
      
      <main className="flex-1 py-8 md:py-12">
        <div className="app-container max-w-6xl">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in">
            <div>
              <h1 className="text-3xl font-semibold text-zinc-900">Your Financial Assessment</h1>
              <p className="text-zinc-500 mt-1">Here's a comprehensive view of your business health</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onReset} data-testid="new-assessment-btn">
                New Assessment
              </Button>
              <Button onClick={() => setEmailModalOpen(true)} className="bg-[#3A5A40] hover:bg-[#344E41]" data-testid="download-report-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Report
              </Button>
            </div>
          </div>

          {/* Health Score */}
          <div className="mb-8">
            <HealthScore score={analysis.metrics.health_score} value={analysis.metrics.health_score_value} />
          </div>

          {/* KPI Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {kpiConfig.map((kpi, index) => (
              <KPICard key={kpi.label} {...kpi} delay={index + 1} />
            ))}
          </div>

          {/* Insights Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-zinc-900 mb-4">Key Insights</h2>
            <Card className="animate-slide-up opacity-0 border-border/60" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }} data-testid="insights-card">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysis.insights.map((insight, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-50">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        insight.status === 'good' ? 'bg-[#65A30D]' : 
                        insight.status === 'warning' ? 'bg-[#EA580C]' : 'bg-[#DC2626]'
                      }`} />
                      <div>
                        <p className="font-medium text-zinc-900">{insight.metric}: <span className="font-mono">{insight.value}</span></p>
                        <p className="text-sm text-zinc-500">{insight.explanation}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recommendations Section */}
          {analysis.recommendations.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-zinc-900 mb-4">Recommendations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.recommendations.map((rec, index) => (
                  <RecommendationCard key={index} recommendation={rec} delay={index} />
                ))}
              </div>
            </div>
          )}

          {/* What Happens Next Section */}
          <Card className="animate-slide-up opacity-0 bg-[#DAD7CD]/30 border-[#3A5A40]/20" style={{ animationDelay: '0.35s', animationFillMode: 'forwards' }} data-testid="what-happens-next">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-zinc-900 mb-2">What Happens Next?</h3>
              <p className="text-zinc-600">
                If you'd like help reviewing your results or building a plan to improve profitability and cash flow, 
                reach out at{" "}
                <a 
                  href="mailto:tim@finnovateadvisors.com" 
                  className="text-[#3A5A40] font-medium hover:underline"
                >
                  tim@finnovateadvisors.com
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-border/40">
        <div className="app-container text-center text-sm text-zinc-500 space-y-2">
          <p>This assessment is for informational purposes only and should not be considered financial advice.</p>
          <p className="flex items-center justify-center gap-2 text-zinc-600">
            <span>Powered by</span>
            <img 
              src="https://customer-assets.emergentagent.com/job_margin-compass-1/artifacts/6le1ffnr_573A34B3-F32D-4F63-B1D8-E89334E6F0DB.jpeg" 
              alt="Finnovate Advisors" 
              className="h-6 w-auto"
            />
          </p>
        </div>
      </footer>

      <EmailModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        analysisId={analysis.id}
        onSuccess={downloadReport}
      />
    </div>
  );
};

// Main Home Component
const Home = () => {
  const [analysis, setAnalysis] = useState(null);

  if (analysis) {
    return <Dashboard analysis={analysis} onReset={() => setAnalysis(null)} />;
  }

  return <FinancialInputForm onAnalyze={setAnalysis} />;
};

// App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
